import { createAction, createAsyncThunk, createReducer } from '@reduxjs/toolkit';
import {
  ESnackbarStyle,
  FormValuesUpdateSchool,
  School,
  SchoolState,
  UpdateSchoolAction,
  UpdateSchoolYoutubeLinks
} from 'shared/types';
import { AppDispatch, RootState } from '../index';
import { addSchool, addSchoolLink, deleteSchool, deleteSchoolLink, getSchoolsList, updateSchool } from 'services/api/schoolsService';
import { PAGE_SIZE, LIMIT_FOR_ALL_ITEMS } from 'shared/constants/pagination';
import { setIsDataLoadingParameter } from './modalReducer';
import { openNotification } from 'utils/notification-utils';

const initialState: SchoolState = {
  allSchools: [],
  schools: [],
  currentPage: 1,
  totalPages: 0,
  totalSchools: 0,
  isSchoolsLoading: true,
  sortBy: ''
};

// Actions

export const clearSchools = createAction('schools/clearSchools');
export const deleteSchoolInAllSchoolList = createAction<string>(
  'schools/deleteSchoolInAllSchoolList'
);
export const addSchoolInAllSchoolList = createAction<School>('schools/addSchoolInAllSchoolList');
export const updateSchoolInAllSchoolList = createAction<UpdateSchoolAction>(
  'schools/updateSchoolInAllSchoolList'
);
export const setIsSchoolsLoading = createAction<boolean>('schools/setIsSchoolsLoading');
export const setCurrentPage = createAction<number>('schools/setCurrentPage');
export const setSchools = createAction<SchoolState>('schools/setSchools');
export const setAllSchools = createAction<School[]>('schools/setAllSchools');
export const setSortBy = createAction<string>('schools/setSortBy');

// Reducer

export const schoolsReducer = createReducer(initialState, (builder): void => {
  builder
    .addCase(setAllSchools, (state, { payload }): void => {
      state.allSchools = payload;
    })
    .addCase(setSchools, (state, { payload }): SchoolState => payload)
    .addCase(setCurrentPage, (state, { payload }): void => {
      state.currentPage = payload;
    })
    .addCase(deleteSchoolInAllSchoolList, (state, { payload }): void => {
      state.allSchools = state.allSchools.filter((school): boolean => school.id !== payload);
    })
    .addCase(addSchoolInAllSchoolList, (state, { payload }): void => {
      const sortedSchools: School[] = [...state.allSchools];

      if (state.allSchools.every((item) => item.id !== payload.id)) {
        sortedSchools.push(payload);
      }

      const filteredSchools = sortedSchools.filter(s => s.name);
      filteredSchools.sort((a, b) => a.name!.localeCompare(b.name!, undefined, { sensitivity: 'base' }));
      state.allSchools = filteredSchools;
    })
    .addCase(updateSchoolInAllSchoolList, (state, { payload }): void => {
      const index = state.allSchools.findIndex((school): boolean => school.id === payload.id);
      if (index !== -1) state.allSchools[index] = { ...state.allSchools[index], ...payload.values };
    })
    .addCase(clearSchools, (): SchoolState => initialState)
    .addCase(setIsSchoolsLoading, (state, { payload }): void => {
      state.isSchoolsLoading = payload;
    })
    .addCase(setSortBy, (state, { payload }): void => {
      state.sortBy = payload;
    });
});

// Thunks

export const fetchAllSchools = createAsyncThunk<
  Promise<void>,
  void,
  { dispatch: AppDispatch; state: RootState }
>('schools/fetchAllSchools', async (_, { dispatch }): Promise<void> => {
  try {
    const response = await getSchoolsList(1, LIMIT_FOR_ALL_ITEMS, { orderBy: 'name,ASC' });
    dispatch(setAllSchools(response.data));
  } catch (e) {
    openNotification(ESnackbarStyle.ERROR, e.message);
  }
});

export const fetchSchools = createAsyncThunk<
  Promise<void>,
  { search?: string } | void,
  { dispatch: AppDispatch; state: RootState }
>('schools/fetchSchools', async (arg = {}, { dispatch, getState }) => {
  const search = arg?.search || '';

  dispatch(setIsSchoolsLoading(true));

  try {
    const schoolsState = getState().schools;
    const response = await getSchoolsList(
      1,
      9999,
      { orderBy: schoolsState.sortBy || 'name,ASC' },
      search,
    );

    dispatch(
      setSchools({
        ...schoolsState,
        allSchools: response.data,
        schools: response.data,
        totalSchools: response.data.length,
        totalPages: 1
      })
    );
  } catch (e) {
    openNotification(ESnackbarStyle.ERROR, e.message);
  } finally {
    dispatch(setIsSchoolsLoading(false));
  }
});

export const updateSchoolItem = createAsyncThunk<
  Promise<void>,
  { schoolId: string; values: FormValuesUpdateSchool },
  { dispatch: AppDispatch }
>('schools/updateSchoolItem', async ({ schoolId, values }, { dispatch }): Promise<void> => {
  dispatch(setIsDataLoadingParameter(true));
  try {
    await updateSchool(schoolId, values);
    await dispatch(fetchSchools());
    dispatch(updateSchoolInAllSchoolList({ id: schoolId, values }));
    openNotification(ESnackbarStyle.SUCCESS, `School updated successfully.`);
  } catch (e) {
    openNotification(ESnackbarStyle.ERROR, e.message);
    return e;
  } finally {
    dispatch(setIsDataLoadingParameter(false));
  }
});

export const addSchoolLinks = createAsyncThunk<
  Promise<void>,
  { schoolId: string; values: UpdateSchoolYoutubeLinks },
  { dispatch: AppDispatch }
>('schools/addSchoolLinks', async ({ schoolId, values }, { dispatch }): Promise<void> => {
  dispatch(setIsDataLoadingParameter(true));
  try {
    await addSchoolLink(schoolId, values);
    await dispatch(fetchSchools());
    dispatch(updateSchoolInAllSchoolList({ id: schoolId, values }));
  } catch (e) {
    openNotification(ESnackbarStyle.ERROR, e.response.data.message);
    return e;
  } finally {
    dispatch(setIsDataLoadingParameter(false));
  }
});

export const deleteSchoolLinks = createAsyncThunk<
  Promise<void>,
  { schoolId: string; values: UpdateSchoolYoutubeLinks },
  { dispatch: AppDispatch }
>('schools/deleteSchoolLinks', async ({ schoolId, values }, { dispatch }): Promise<void> => {
  dispatch(setIsDataLoadingParameter(true));
  try {
    await deleteSchoolLink(schoolId, values);
    await dispatch(fetchSchools());
    dispatch(updateSchoolInAllSchoolList({ id: schoolId, values }));
  } catch (e) {
    openNotification(ESnackbarStyle.ERROR, e.response.data.message);
    return e;
  } finally {
    dispatch(setIsDataLoadingParameter(false));
  }
});

export const addNewSchool = createAsyncThunk<
  Promise<void>,
  { values: FormValuesUpdateSchool },
  { dispatch: AppDispatch }
>('schools/addNewSchool', async ({ values }, { dispatch }): Promise<void> => {
  dispatch(setIsDataLoadingParameter(true));
  try {
    const response = await addSchool(values);
    await dispatch(fetchSchools());
    dispatch(addSchoolInAllSchoolList(response.data));
    openNotification(ESnackbarStyle.SUCCESS, `School created successfully.`);
  } catch (e) {
    openNotification(ESnackbarStyle.ERROR, e.message);
  } finally {
    dispatch(setIsDataLoadingParameter(false));
  }
});

export const removeSchoolItem = createAsyncThunk<
  Promise<void>,
  { schoolId: string },
  { dispatch: AppDispatch; state: RootState }
>('schools/fetchSchools', async ({ schoolId }, { dispatch, getState }): Promise<void> => {
  dispatch(setIsDataLoadingParameter(true));
  try {
    await deleteSchool(schoolId);
    await dispatch(fetchSchools());
    dispatch(deleteSchoolInAllSchoolList(schoolId));
    if (getState().schools.totalSchools <= PAGE_SIZE) dispatch(setCurrentPage(1));
  } catch (e) {
    openNotification(ESnackbarStyle.ERROR, e.message);
    return e;
  } finally {
    dispatch(setIsDataLoadingParameter(false));
  }
});
